// Components — docs/ARCHITECTURE.md §4. Rules (game-engine/constrains/esengine-coding-constrain.md):
// pure data, primitives only, every field initialized, SimId numbers instead of entity references.
// `@sync` marks what views carry; private player data is never @sync (it travels on the private channel).
// Requires tsconfig `useDefineForClassFields: false`: @sync installs prototype accessors.

import { Component, ECSComponent, Serializable, Serialize, sync, type ComponentType } from '@esengine/ecs-framework'

@ECSComponent('SimIdentity')
@Serializable({ version: 1, typeId: 'SimIdentity' })
export class SimIdentity extends Component {
  @Serialize() @sync('uint32') simId = 0
}

@ECSComponent('Owner')
@Serializable({ version: 1, typeId: 'Owner' })
export class Owner extends Component {
  /** OwnerValue: a seat (PlayerId) or NEUTRAL_OWNER (-1). */
  @Serialize() @sync('int8') player = -1
}

@ECSComponent('GeoPosition')
@Serializable({ version: 1, typeId: 'GeoPosition' })
export class GeoPosition extends Component {
  @Serialize() @sync('float32') lat = 0
  @Serialize() @sync('float32') lon = 0
  /** Radians clockwise from north. */
  @Serialize() @sync('float32') heading = 0
}

@ECSComponent('GraphTraversal')
@Serializable({ version: 2, typeId: 'GraphTraversal' })
export class GraphTraversal extends Component {
  /** CityIndex values. The client interpolates GeoPosition instead of these. */
  @Serialize() fromCity = 0
  @Serialize() toCity = 0
  /** 0..1 progress along the edge. */
  @Serialize() t = 0
  /** DomainCode of the network this traversal moves on: its unit's content domain (a craft's mode domain), fixed at spawn. */
  @Serialize() domain = 0
}

@ECSComponent('Health')
@Serializable({ version: 1, typeId: 'Health' })
export class Health extends Component {
  @Serialize() @sync('float32') hp = 0
  @Serialize() @sync('float32') maxHp = 0
}

@ECSComponent('CityNode')
@Serializable({ version: 1, typeId: 'CityNode' })
export class CityNode extends Component {
  /** CityIndex in the WarMap. */
  @Serialize() @sync('uint16') cityIndex = 0
  @Serialize() @sync('boolean') isCapital = false
  @Serialize() @sync('boolean') coastal = false
  @Serialize() @sync('boolean') fallen = false
  /** 0..1 capture progress while fallen. */
  @Serialize() @sync('float32') captureProgress = 0
  /** OwnerValue of the side currently capturing. */
  @Serialize() @sync('int8') captureBy = -1
  @Serialize() buildingSlots = 0
}

@ECSComponent('Building')
@Serializable({ version: 1, typeId: 'Building' })
export class Building extends Component {
  /** BuildingTypeId. */
  @Serialize() @sync('uint8') typeId = 0
  @Serialize() @sync('uint32') citySimId = 0
}

@ECSComponent('UnitKind')
@Serializable({ version: 1, typeId: 'UnitKind' })
export class UnitKind extends Component {
  /** UnitTypeId. */
  @Serialize() @sync('uint8') typeId = 0
  /** Living bodies (squads/swarms lose bodies as they take damage). */
  @Serialize() @sync('uint8') bodies = 0
}

@ECSComponent('Weapon')
@Serializable({ version: 2, typeId: 'Weapon' })
export class Weapon extends Component {
  @Serialize() damage = 0
  /** Seconds between attacks. */
  @Serialize() period = 0
  @Serialize() range = 0
  @Serialize() minRange = 0
  @Serialize() splash = 0
  /** TargetClassCode. */
  @Serialize() targetClass = 0
  /** Ticks until the next attack is ready. Owned by WeaponSystem. */
  @Serialize() cooldownTicks = 0
  /** SimId of the locked target; 0 = none. Behaviour/guns select locks; embarking clears a passenger's lock. */
  @Serialize() @sync('uint32') lockedTarget = 0
  /**
   * Attacks made, wrapping at SHOT_COUNTER_WRAP. Written only by WeaponSystem, once per attack; the client plays one
   * attack effect per advance (it changes only when the weapon fires, so it costs no bandwidth otherwise).
   */
  @Serialize() @sync('uint8') shots = 0
}

/** Weapon.shots wraps here (it is synced as a uint8). */
export const SHOT_COUNTER_WRAP = 256

@ECSComponent('Mobility')
@Serializable({ version: 1, typeId: 'Mobility' })
export class Mobility extends Component {
  /** mu per second. */
  @Serialize() speed = 0
  /** DomainCode the unit moves in. */
  @Serialize() domain = 0
  /** Ultimate CityIndex the unit is heading for; -1 = none. Behaviour sets it; embarking clears it. */
  @Serialize() goalCity = -1
  /** SimId the unit pursues directly (air units / in-sight pursuit); 0 = none. */
  @Serialize() goalTarget = 0
}

@ECSComponent('Spawner')
@Serializable({ version: 1, typeId: 'Spawner' })
export class Spawner extends Component {
  /** UnitTypeId spawned. */
  @Serialize() typeId = 0
  @Serialize() intervalTicks = 0
  @Serialize() maxAlive = 0
  @Serialize() ticksUntilNext = 0
}

@ECSComponent('Sight')
@Serializable({ version: 1, typeId: 'Sight' })
export class Sight extends Component {
  /** mu. */
  @Serialize() radius = 0
}

@ECSComponent('StrikeFlight')
@Serializable({ version: 2, typeId: 'StrikeFlight' })
export class StrikeFlight extends Component {
  /** StrikeTypeId. */
  @Serialize() @sync('uint8') typeId = 0
  /** Where it is headed: its homing target's position this tick, else the point it was aimed at. */
  @Serialize() @sync('float32') targetLat = 0
  @Serialize() @sync('float32') targetLon = 0
  /** The launch city. */
  @Serialize() @sync('float32') originLat = 0
  @Serialize() @sync('float32') originLon = 0
  /** 1 once it reached its target (it rests there for its remaining salvos); 0 while it flies. */
  @Serialize() @sync('uint8') onTarget = 0
  /** Ticks from arrival to the pending salvo; they count down only once on target (salvo 0 lands on arrival). */
  @Serialize() ticksLeft = 0
  /** 0-based salvo index for multi-salvo strikes. */
  @Serialize() @sync('uint8') salvo = 0
  /** SimId of the entity it homes on; 0 = none (it flies to a point, or its target is gone). Sim only. */
  @Serialize() homingTarget = 0
}

@ECSComponent('Demoralized')
@Serializable({ version: 1, typeId: 'Demoralized' })
export class Demoralized extends Component {
  @Serialize() untilTick = 0
  @Serialize() damageMultiplier = 1
}

/**
 * An automatic transport craft (taxgw-8lvi, taxgw-rvwq): a Port's boat (sea domain) or an Airbase's plane (air domain),
 * unarmed but targetable, carrying ground units. It has no UnitKind, Weapon, Spawner or behaviour tree.
 * Owner/yaw/HP/sight live in the shared components; this adds only the trip state. Presentation-relevant fields are
 * synced; the rest are authoritative.
 */
@ECSComponent('TransportHull')
@Serializable({ version: 3, typeId: 'TransportHull' })
export class TransportHull extends Component {
  /** TransportModeCode, fixed at spawn from the berth that built it: a boat or a plane (v3). */
  @Serialize() @sync('uint8') mode = 0
  /**
   * SimId of the berth building (Port or Airbase) this craft was built for: its owner for life. The craft never changes
   * home, so a berth rebuilt at the same city (a new SimId) never adopts it; a craft whose exact home building is gone
   * is an orphan that finishes its delivery, returns to homeCity and retires. Always a real SimId (spawn sets it); 0 is
   * corrupt.
   */
  @Serialize() homePortSimId = 0
  /** CityIndex the craft was built at (its home berth's city). */
  @Serialize() homeCity = -1
  /** TransportPhase. idle has no destination; pickup travels to the reserved group's pickup city. */
  @Serialize() @sync('uint8') phase = 0
  /** CityIndex the craft is travelling to: the pickup city in pickup, the landing in outbound, home in returning,
   * -1 (NO_GOAL_CITY) in idle. */
  @Serialize() destinationCity = -1
  /** Cargo slots in use (0 = empty). Passengers occupy whole slot counts; the graphic shows used/capacity. */
  @Serialize() @sync('uint8') cargoSlotsUsed = 0
  /**
   * Sim-only: the SOLE authoritative reservation of a booked group (queued, or walking in just in time) to this hull
   * (0 = none). Set only while the hull
   * is in pickup phase, and cleared before it leaves pickup (boarding or invalidation). The derived reservation
   * lookup (world/transport-projection.ts) is rebuilt from this field; a passenger never stores a second copy.
   */
  @Serialize() pickupUnitSimId = 0
}

/**
 * A ground unit's transport intent (taxgw-8lvi, taxgw-rvwq, taxgw-6gcs). Attached only to land units at spawn. A trip
 * is a booking: the unit commits to one craft leg (mode, pickup, landing) toward its objective, walks to the pickup
 * (`bound`), waits there (`queued`), rides (`embarked`) and lands ashore. `ashore`, `needsTransport`, `bound` and
 * `queued` units all act independently (attack, capture, contest, take damage, give sight, be targeted); only an
 * `embarked` unit is cargo and takes part in nothing until it lands.
 */
@ECSComponent('TransportPassenger')
@Serializable({ version: 5, typeId: 'TransportPassenger' })
export class TransportPassenger extends Component {
  /** PassengerState. */
  @Serialize() @sync('uint8') state = 0
  /** TransportModeCode of the booked trip (bound, queued or embarked): which craft the group rides (v4). Meaningless ashore. */
  @Serialize() mode = 0
  /** SimId of the hull carrying it while embarked; 0 otherwise. */
  @Serialize() @sync('uint32') boatSimId = 0
  /** CityIndex of the pickup city the unit walks to and waits at (a served coast, or an own airbase city); -1 when it has no trip. */
  @Serialize() pickupCity = -1
  /** CityIndex the passenger intends to land at; -1 when undecided/none. */
  @Serialize() destinationCity = -1
  /** CityIndex the unit ultimately wants to reach; -1 when undecided/none. The boarding revalidation ties the
   * landing back to this objective, so a stale voyage never boards. Serialized in v2. */
  @Serialize() objectiveCity = -1
  /** Tick the unit joined its pickup's queue (FIFO order is (orderTick, SimId)); retained while the booking holds. */
  @Serialize() orderTick = 0
  /**
   * Sim-only (v5): the booked craft leg's one-way travel time in ticks, priced when the trip was booked. The transport
   * load (world/transport-load.ts) charges the group's pool a round trip of it per craft load while it is booked.
   */
  @Serialize() legTicks = 0
}

/**
 * Per-berth transport fleet bookkeeping (taxgw-8lvi, taxgw-rvwq, taxgw-6gcs), on the building that provides the berth
 * (a Port or an Airbase). The berth replaces lost craft at most one per its mode's TRANSPORT_MODES replacementSeconds;
 * replacementReadyTick is the first tick the next replacement may launch (0 = ready now).
 */
@ECSComponent('TransportFleet')
@Serializable({ version: 3, typeId: 'TransportFleet' })
export class TransportFleet extends Component {
  /**
   * Sim-only: the first tick the berth may launch the next replacement. The live craft count is NOT stored here: it
   * is rebuilt each tick by the world's transport projection from TransportHull.homePortSimId (outbound and returning
   * craft included), so a lost craft can never desync the fleet bookkeeping.
   */
  @Serialize() replacementReadyTick = 0
  /** Sim-only: a brand-new berth supplies its whole complement at once, then only replaces losses. */
  @Serialize() seeded = false
  /**
   * Sim-only (v3): the priced wait band of the berth's transport pool (world/transport-load.ts), the one piece of the
   * pool's price that is memory rather than a function of the current state (its hysteresis). Every berth of a pool
   * carries the same value, written each tick; the pool's oldest berth (lowest SimId) is read back, so a berth built
   * since the last tick never overrides it.
   */
  @Serialize() waitBand = 0
}

/** Private: sent only to its owner through PlayerPrivateState. */
@ECSComponent('PlayerEconomy')
@Serializable({ version: 1, typeId: 'PlayerEconomy' })
export class PlayerEconomy extends Component {
  @Serialize() player = 0
  @Serialize() money = 0
  /** Last computed revenue per second (after tax, approval and sanctions). */
  @Serialize() revenuePerSec = 0
  /** TaxCode. */
  @Serialize() tax = 2
  @Serialize() approval = 0
  @Serialize() sanctionedUntilTick = 0
  @Serialize() sanctionRevenueMultiplier = 1
}

/** Private: War Ledger (GDD §6). */
@ECSComponent('Ledger')
@Serializable({ version: 1, typeId: 'Ledger' })
export class Ledger extends Component {
  @Serialize() player = 0
  @Serialize() spentUnits = 0
  @Serialize() spentBuildings = 0
  @Serialize() spentStrikes = 0
  @Serialize() lostValue = 0
  @Serialize() citiesCaptured = 0
  @Serialize() citiesLost = 0
}

/** Singleton on the match entity. Per-seat host state (last command seq) lives on each seat's private entity. */
@ECSComponent('MatchState')
@Serializable({ version: 2, typeId: 'MatchState' })
export class MatchState extends Component {
  @Serialize() @sync('uint32') tick = 0
  /** MatchPhase. */
  @Serialize() @sync('uint8') phase = 0
  @Serialize() @sync('uint32') phaseEndTick = 0
  /** OutcomeKind. */
  @Serialize() @sync('uint8') outcome = 0
  /** OwnerValue of the winner; -1 while running or on a draw. */
  @Serialize() @sync('int8') winner = -1
  @Serialize() nextSimId = 1
  @Serialize() seed = 0
  /** xoshiro128** state. */
  @Serialize() rng0 = 0
  @Serialize() rng1 = 0
  @Serialize() rng2 = 0
  @Serialize() rng3 = 0
}

/** Every component class, in registration order. Register all before creating entities. */
export const ALL_COMPONENTS: readonly ComponentType<Component>[] = [
  SimIdentity,
  Owner,
  GeoPosition,
  GraphTraversal,
  Health,
  CityNode,
  Building,
  UnitKind,
  Weapon,
  Mobility,
  Spawner,
  Sight,
  StrikeFlight,
  Demoralized,
  PlayerEconomy,
  Ledger,
  MatchState,
  TransportHull,
  TransportPassenger,
  TransportFleet,
]
